import React, { useState, useEffect } from 'react';
import { useAgreement } from '../context/AgreementContext';
import { Preferences } from '../types';

interface PreferencesStepProps {
  onNext: (preferences: Preferences) => void; // Modified to pass preferences
  onBack: () => void;
  agreementStatus?: string | null;
}

export function PreferencesStep({ onNext, onBack, agreementStatus }: PreferencesStepProps) {
  const { state, dispatch } = useAgreement();
  const { preferences } = state;
  
  // Set input fields to read-only when agreement is confirmed
  const isConfirmed = agreementStatus === 'confirmed';
  
  console.log("PreferencesStep - Context preferences:", preferences);

  const [localPreferences, setLocalPreferences] = useState<Preferences>({
    maxPrice: 1000000,
    propertyPreferences: ''
  });
  
  console.log("PreferencesStep - Local preferences initialized:", localPreferences);

  // Monitor changes to preferences in context and update local state
  useEffect(() => {
    console.log("PreferencesStep - Context preferences changed:", preferences);
    if (preferences) {
      console.log("PreferencesStep - Updating local preferences from context:", preferences);
      setLocalPreferences(preferences);
    }
  }, [preferences]);

  // Initialize local preferences when component mounts if context preferences exist
  useEffect(() => {
    if (preferences) {
      console.log("PreferencesStep - Initial load: setting local preferences from context:", preferences);
      setLocalPreferences(preferences);
    }
  }, []); // Only run on mount

  const handleMaxPriceChange = (value: string) => {
    const numValue = parseInt(value.replace(/,/g, '')) || 0;
    setLocalPreferences(prev => ({
      ...prev,
      maxPrice: numValue
    }));
  };

  const handlePreferencesChange = (value: string) => {
    console.log("PreferencesStep - Property preferences changed to:", value);
    setLocalPreferences(prev => ({
      ...prev,
      propertyPreferences: value
    }));
  };

  const handleNext = () => {
    // If agreement is confirmed, just proceed without updating data
    if (isConfirmed) {
      console.log("PreferencesStep - Agreement is confirmed, proceeding without data updates");
      onNext(localPreferences);
      return;
    }
    
    console.log("PreferencesStep - Dispatching preferences to context:", localPreferences);
    
    // Create a deep copy to ensure we're not affected by reference issues
    const preferencesCopy = JSON.parse(JSON.stringify(localPreferences));
    console.log("PreferencesStep - Deep copy of preferences:", preferencesCopy);
    
    // Dispatch the preferences update
    dispatch({ type: 'SET_PREFERENCES', payload: preferencesCopy });
    
    // Pass the preferences directly to the parent component
    console.log("PreferencesStep - Passing preferences directly to parent:", preferencesCopy);
    onNext(preferencesCopy);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const isValid = localPreferences.maxPrice > 0 && localPreferences.propertyPreferences.trim().length > 0;
  console.log("PreferencesStep - Validation:", {
    maxPrice: localPreferences.maxPrice,
    propertyPreferencesLength: localPreferences.propertyPreferences.trim().length,
    isValid
  });

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 h-fit">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">
          Property preferences?
        </h2>
        <p className="text-sm text-gray-600">
          Help us find the perfect property match.
        </p>
      </div>

      <div className="space-y-6">
        {/* Max Purchase Price */}
        <div>
          <h3 className="text-base font-semibold text-gray-900 mb-3">Maximum Purchase Price</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max Price
            </label>
            <input
              type="text"
              value={formatPrice(localPreferences.maxPrice).replace('$', '')}
              onChange={(e) => !isConfirmed && handleMaxPriceChange(e.target.value)}
              readOnly={isConfirmed}
              className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm ${
                isConfirmed ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
              placeholder="1,000,000"
            />
            <div className="mt-2 text-xs text-gray-600">
              Maximum budget: {formatPrice(localPreferences.maxPrice)}
            </div>
          </div>
        </div>

        {/* Property Preferences */}
        <div>
          <h3 className="text-base font-semibold text-gray-900 mb-3">Property Preferences</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Describe your ideal property *
            </label>
            <textarea
              value={localPreferences.propertyPreferences}
              onChange={(e) => !isConfirmed && handlePreferencesChange(e.target.value)}
              readOnly={isConfirmed}
              rows={6}
              className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm resize-none ${
                isConfirmed ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
              placeholder="Please describe your property preferences if you have any."
            />
            <div className="mt-2 text-xs text-gray-500">
              {localPreferences.propertyPreferences.length}/500 characters
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center gap-4 mt-6">
        <button
          onClick={onBack}
          className="px-5 py-2.5 border border-gray-300 rounded-full font-semibold text-gray-700 hover:bg-gray-50 transition-all duration-200 min-h-[42px]"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          disabled={!isValid}
          className={`px-6 py-2.5 rounded-full font-semibold transition-all duration-200 min-h-[42px] ${
            isValid
              ? 'bg-primary-500 hover:bg-primary-600 text-white'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          Continue
        </button>
      </div>
    </div>
  );
}